import { createSignal, createResource, createMemo, onMount, For, Show, Index } from 'solid-js';
import { useActiveBot } from '../../state/activeBot';
import { useBotFanOut } from '../../hooks/useBotFanOut';
import { BOT_ACTIONS } from '../../services/resourceActions';
import ItemStackView from '../../components/ItemStackView';
import QuantityInput from '../../components/QuantityInput';
import StorageItemPicker from '../../components/StorageItemPicker';
import OrbsTraderNav from '../../components/OrbsTraderNav';
import { useApi, isOk } from '../../api';
import { showConfirm } from '../../components/Dialog';
import { useToast } from '../../components/MessageBox';
import { useInventoryApi, type StorageInfo } from '../../services/inventoryApi';
import { Citybuild } from '../../models/Citybuild';
import Text from '../../components/Text';
import { useTranslation } from '../../hooks/useTranslation';
import { useItemName } from '../../hooks/useItemName';
import BotScoped from '../../components/BotScoped';
import StorageSelect from '../../components/StorageSelect';
import { formatDateTime } from '../../services/dateFormat';
import { formatQuantity } from '../../services/quantityFormatter';
import { formatInteger } from '../../services/numberFormat';
import { parseItemKey } from '../../services/craftingResolver';
import { firstError } from '../../api/envelope';
import type { OrbsSellConfigDto, OrbsSellLine } from '../../api/generated/schemas';
import type { ItemStack } from '../../models/itemStack';
import styles from './Demolish.module.css';
import orbStyles from './OrbsTrader.module.css';

const citybuildOptions = Object.keys(Citybuild).filter(k => isNaN(Number(k))) as (keyof typeof Citybuild)[];
const OUTCOME_COMPLETED = 1;

export default function OrbsSellPage() {
    return <BotScoped groupAware><OrbsSellInner /></BotScoped>;
}

function OrbsSellInner() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { itemName, stackSize } = useItemName();
    const inv = useInventoryApi();

    const [configs, setConfigs] = createSignal<OrbsSellConfigDto[]>([]);
    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const [loading, setLoading] = createSignal(true);

    const [showForm, setShowForm] = createSignal(false);
    const [editId, setEditId] = createSignal<string | null>(null);
    const [formName, setFormName] = createSignal('');
    const [formStorageId, setFormStorageId] = createSignal('');
    const [formCitybuild, setFormCitybuild] = createSignal<number | null>(null);
    const [formAllowBelowBestPrice, setFormAllowBelowBestPrice] = createSignal(false);
    const [formLines, setFormLines] = createSignal<OrbsSellLine[]>([]);
    const [saving, setSaving] = createSignal(false);

    const { botId: activeBotId, targetIds } = useActiveBot();
    const fanOut = useBotFanOut();
    const botId = () => activeBotId() ?? '';

    const [storageItems] = createResource(
        () => showForm() && formStorageId() ? formStorageId() : null,
        storageId => inv.fetchStorageItems(storageId));
    // Compressed units come out of infinite chests by base item; a compressed stack in an ordinary chest cannot be taken.
    const sellableStacks = createMemo(() =>
        (storageItems()?.stacks ?? []).filter(stack => !stack.snbt?.includes('compressionLevel')));

    onMount(async () => {
        try {
            const [cfgRes, storageList] = await Promise.all([
                api.orbsTrader.getOrbsSellConfigs(),
                inv.fetchStorages(),
            ]);
            if (isOk(cfgRes.data)) setConfigs(cfgRes.data.value.configs ?? []);
            else toast?.error(firstError(cfgRes.data) ?? t('ORBS_SELL_LOAD_FAILED'));
            setStorages(storageList);
        } catch (e: any) {
            toast?.error(e?.message ?? t('ORBS_SELL_LOAD_FAILED'));
        } finally { setLoading(false); }
    });

    async function loadConfigs() {
        const res = await api.orbsTrader.getOrbsSellConfigs();
        if (isOk(res.data)) setConfigs(res.data.value.configs ?? []);
    }

    function openCreate() {
        setEditId(null);
        setFormName('');
        setFormStorageId(storages()[0]?.id ?? '');
        setFormCitybuild(null);
        setFormAllowBelowBestPrice(false);
        setFormLines([{ itemKey: '', amount: null }]);
        setShowForm(true);
    }

    function openEdit(c: OrbsSellConfigDto) {
        setEditId(c.id);
        setFormName(c.name);
        setFormStorageId(c.storageId);
        setFormCitybuild(c.citybuild ?? null);
        setFormAllowBelowBestPrice(c.allowBelowBestPrice);
        setFormLines(c.lines.map(line => ({ ...line })));
        setShowForm(true);
    }

    const stockOf = (itemKey: string) => sellableStacks().find(stack => stack.key === itemKey)?.count ?? 0;

    function updateLine(index: number, patch: Partial<OrbsSellLine>) {
        setFormLines(lines => lines.map((line, i) => i === index ? { ...line, ...patch } : line));
    }

    async function save(e: Event) {
        e.preventDefault();
        if (!formStorageId()) { toast?.error(t('ORBS_SELL_SELECT_STORAGE')); return; }
        if (formLines().length === 0 || formLines().some(line => !line.itemKey || line.amount == null)) { toast?.error(t('ORBS_SELL_PICK_ITEMS')); return; }
        setSaving(true);
        const body = {
            name: formName().trim() || t('ORBS_SELL_DEFAULT_NAME'),
            storageId: formStorageId(),
            citybuild: formCitybuild(),
            allowBelowBestPrice: formAllowBelowBestPrice(),
            lines: formLines(),
        };
        try {
            const id = editId();
            const res = id
                ? await api.orbsTrader.updateOrbsSellConfig(id, body)
                : await api.orbsTrader.createOrbsSellConfig(body);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('ORBS_SELL_SAVE_FAILED'));
                return;
            }
            setShowForm(false);
            toast?.info(id ? t('ORBS_SELL_UPDATED') : t('ORBS_SELL_CREATED'));
            await loadConfigs();
        } finally { setSaving(false); }
    }

    async function deleteConfig(id: string) {
        const confirmed = await showConfirm(t('ORBS_SELL_DELETE_CONFIRM'), true);
        if (!confirmed) return;
        const res = await api.orbsTrader.deleteOrbsSellConfig(id);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('ORBS_SELL_DELETE_FAILED'));
            return;
        }
        toast?.info(t('ORBS_SELL_DELETED'));
        await loadConfigs();
    }

    async function runConfig(id: string) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('ORBS_TRADER_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.orbsTrader.runOrbsSellConfig(id, { botId: targetBotId }),
            { success: t('ORBS_SELL_STARTED'), failure: t('ORBS_SELL_START_FAILED') });
    }

    async function stopConfig(id: string) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('ORBS_TRADER_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.orbsTrader.stopOrbsSell(id, { botId: targetBotId }),
            { success: t('ORBS_SELL_STOPPED'), failure: t('ORBS_SELL_STOP_FAILED') });
    }

    function storageName(id: string) {
        return storages().find(s => s.id === id)?.name ?? id.slice(0, 8);
    }

    function citybuildLabel(c: OrbsSellConfigDto) {
        return c.citybuild != null ? Citybuild[c.citybuild] : t('ORBS_TRADER_CITYBUILD_FROM_STORAGE');
    }

    function stackOfKey(itemKey: string): ItemStack | null {
        const parsed = parseItemKey(itemKey);
        return parsed ? { itemId: parsed.id, damage: parsed.metadata, count: 1 } : null;
    }

    function amountLabel(itemKey: string, amount: number | null | undefined) {
        return amount == null ? t('ORBS_SELL_ALL') : formatQuantity(amount, stackSize(itemKey));
    }

    return (
        <div class={styles.container}>
            <OrbsTraderNav />
            <div class={styles.headerRow}>
                <h1><Text val="ORBS_SELL_TITLE" /></h1>
                <button class="btn btn-primary" onClick={openCreate} disabled={loading()}>
                    <i class="fa-solid fa-plus" /> <Text val="ORBS_SELL_NEW_CONFIG" />
                </button>
            </div>
            <p class={styles.subtitle}><Text val="ORBS_SELL_SUBTITLE" /></p>

            <Show when={loading()}>
                <div class={styles.empty}>
                    <i class="fa-solid fa-spinner fa-spin fa-2x" />
                </div>
            </Show>

            <Show when={!loading()}>
                <Show when={showForm()}>
                    <form class={`fcard ${styles.form}`} onSubmit={save}>
                        <h2 class={styles.formTitle}>
                            {editId() ? <Text val="ORBS_SELL_EDIT" /> : <Text val="ORBS_SELL_CREATE" />}
                        </h2>

                        <div class={styles.formGrid}>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="ORBS_TRADER_F_NAME" /></span>
                                <input class={styles.input} value={formName()} onInput={e => setFormName(e.currentTarget.value)} placeholder={t('ORBS_SELL_NAME_PLACEHOLDER')} />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="ORBS_SELL_F_STORAGE" /></span>
                                <StorageSelect
                                    storages={storages()}
                                    value={formStorageId()}
                                    onChange={setFormStorageId}
                                    placeholder={t('ORBS_TRADER_SELECT_STORAGE_OPTION')}
                                />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="ORBS_SELL_F_CITYBUILD" /></span>
                                <select
                                    class={styles.input}
                                    value={formCitybuild() ?? ''}
                                    onChange={e => setFormCitybuild(e.currentTarget.value === '' ? null : parseInt(e.currentTarget.value))}
                                >
                                    <option value="">{t('ORBS_TRADER_CITYBUILD_FROM_STORAGE')}</option>
                                    <For each={citybuildOptions}>{(cb) => <option value={Citybuild[cb]}>{cb}</option>}</For>
                                </select>
                            </label>
                        </div>

                        <label class={styles.checkField}>
                            <input
                                type="checkbox"
                                checked={formAllowBelowBestPrice()}
                                onChange={e => setFormAllowBelowBestPrice(e.currentTarget.checked)}
                            />
                            <Text val="ORBS_SELL_F_ALLOW_BELOW_BEST" />
                        </label>
                        <span class={styles.fieldHint}><Text val="ORBS_SELL_F_ALLOW_BELOW_BEST_HINT" /></span>

                        <div class={styles.field}>
                            <span class={styles.fieldLabel}><Text val="ORBS_SELL_F_ITEMS" /></span>
                            <Show when={!storageItems.loading} fallback={<i class="fa-solid fa-spinner fa-spin" />}>
                                <div class={orbStyles.lines}>
                                    <Index each={formLines()}>
                                        {(line, index) => (
                                            <div class={orbStyles.sellLineRow}>
                                                <StorageItemPicker
                                                    stacks={sellableStacks()}
                                                    value={line().itemKey}
                                                    onChange={itemKey => updateLine(index, { itemKey })}
                                                    placeholder={t('ORBS_SELL_PICK_ITEM')}
                                                />
                                                <QuantityInput min={1} value={line().amount ?? 0} stackSize={stackSize(line().itemKey)}
                                                    max={stockOf(line().itemKey) || undefined}
                                                    onChange={amount => updateLine(index, { amount })}
                                                    placeholder={t('ORBS_SELL_F_AMOUNT')} />
                                                <button type="button" class="btn btn-danger btn-sm btn-icon" title={t('ORBS_TRADER_REMOVE_ITEM')} onClick={() => setFormLines(lines => lines.filter((_, i) => i !== index))}>
                                                    <i class="fa-solid fa-trash" />
                                                </button>
                                            </div>
                                        )}
                                    </Index>
                                </div>
                            </Show>
                            <div>
                                <button type="button" class="btn btn-secondary btn-sm" onClick={() => setFormLines(lines => [...lines, { itemKey: '', amount: null }])}>
                                    <i class="fa-solid fa-plus" /> <Text val="ORBS_TRADER_ADD_ITEM" />
                                </button>
                            </div>
                        </div>

                        <div class={styles.formActions}>
                            <button type="button" class="btn btn-secondary" onClick={() => setShowForm(false)} disabled={saving()}>
                                <Text val="CANCEL" />
                            </button>
                            <button type="submit" class="btn btn-primary" disabled={saving() || !formStorageId() || formLines().length === 0}>
                                <Show when={saving()} fallback={<><i class="fa-solid fa-floppy-disk" /> <Text val="SAVE" /></>}>
                                    <i class="fa-solid fa-spinner fa-spin" /> <Text val="SAVE" />
                                </Show>
                            </button>
                        </div>
                    </form>
                </Show>

                <Show when={configs().length === 0 && !showForm()}>
                    <div class={styles.empty}>
                        <i class="fa-solid fa-coins" />
                        <p><Text val="ORBS_SELL_EMPTY" /></p>
                    </div>
                </Show>

                <div class={styles.grid}>
                    <For each={configs()}>
                        {(c) => (
                            <div class={`fcard ${styles.card}`}>
                                <div class={styles.cardHeader}>
                                    <div>
                                        <div class={styles.cardName}>{c.name}</div>
                                        <div class={styles.cardMeta}>{storageName(c.storageId)} · {citybuildLabel(c)}</div>
                                    </div>
                                </div>

                                <div class={styles.cardBody}>
                                    <For each={c.lines}>
                                        {(line) => (
                                            <div class={styles.infoRow}>
                                                <Show when={stackOfKey(line.itemKey)} fallback={<i class="fa-solid fa-circle-question" />}>
                                                    {(stack) => <ItemStackView stack={stack()} size={20} bare />}
                                                </Show>
                                                <span>{itemName(line.itemKey)}</span>
                                                <span class={`${orbStyles.amount} ${orbStyles.push}`}>{amountLabel(line.itemKey, line.amount)}</span>
                                            </div>
                                        )}
                                    </For>
                                    <div class={orbStyles.muted}>
                                        <Text val={c.allowBelowBestPrice ? 'ORBS_SELL_BELOW_BEST_ALLOWED' : 'ORBS_SELL_BEST_PRICE_ONLY'} />
                                    </div>
                                </div>

                                <Show when={c.lastRun}>
                                    {(run) => (
                                        <div class={orbStyles.lastRun}>
                                            <div class={orbStyles.lastRunHeader}>
                                                <Text val="ORBS_TRADER_LAST_RUN" />
                                                <span class={orbStyles.muted}>
                                                    {formatDateTime(run().startedAt)}
                                                    {run().finishedAt ? '' : ` · ${t('ORBS_TRADER_LAST_RUN_RUNNING')}`}
                                                </span>
                                            </div>
                                            <For each={run().lines}>
                                                {(result) => (
                                                    <div class={orbStyles.summaryRow}>
                                                        <span>
                                                            {itemName(result.itemKey)}{' '}
                                                            {result.requested == null
                                                                ? t('ORBS_SELL_SOLD', formatInteger(result.sold))
                                                                : t('ORBS_SELL_SOLD_OF', formatInteger(result.sold), formatInteger(result.requested))}
                                                        </span>
                                                        <span class={result.outcome === OUTCOME_COMPLETED ? orbStyles.muted : orbStyles.short}>
                                                            {t(`ORBS_SELL_OUTCOME_${result.outcome}`)}
                                                        </span>
                                                    </div>
                                                )}
                                            </For>
                                            <div class={orbStyles.muted}>{t('ORBS_SELL_LAST_RUN_EARNED', formatInteger(run().orbsEarned))}</div>
                                            <Show when={run().returnFailed}>
                                                <div class={orbStyles.short}><Text val="ORBS_SELL_LAST_RUN_RETURN_FAILED" /></div>
                                            </Show>
                                        </div>
                                    )}
                                </Show>

                                <div class={styles.cardActions}>
                                    <button class="btn btn-primary" onClick={() => runConfig(c.id)} disabled={!botId()} title={t('RUN')}>
                                        <i class="fa-solid fa-play" /> <Text val="ORBS_SELL_RUN" />
                                    </button>
                                    <button class="btn btn-secondary" onClick={() => stopConfig(c.id)} disabled={!botId()} title={t('STOP')}>
                                        <i class="fa-solid fa-stop" />
                                    </button>
                                    <button class="btn btn-secondary" onClick={() => openEdit(c)} title={t('EDIT')}>
                                        <i class="fa-solid fa-pen" />
                                    </button>
                                    <button class="btn btn-danger" onClick={() => deleteConfig(c.id)} title={t('DELETE')}>
                                        <i class="fa-solid fa-trash" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </For>
                </div>
            </Show>
        </div>
    );
}
